import React, { useEffect, useState, useCallback } from "react";
import {
  TextField,
  Button,
  CircularProgress,
  Box,
  FormHelperText,
  IconButton,
  Typography,
  Autocomplete,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setSuccess } from "../../../Redux/AlertReducer";
import { Helper } from "../../../tools/Helper";
import { api_Routes } from "../../../tools/api_Routes";
import { debounce } from 'lodash';

// Custom image handler for base64
const ImageHandler = function() {
  const input = document.createElement('input');
  input.setAttribute('type', 'file');
  input.setAttribute('accept', 'image/*');
  input.click();

  input.onchange = () => {
    const file = input.files[0];
    const reader = new FileReader();

    reader.onload = (e) => {
      const range = this.quill.getSelection();
      this.quill.insertEmbed(range.index, 'image', e.target.result);
    };

    reader.readAsDataURL(file);
  };
};

// Custom video handler for base64
const VideoHandler = function() {
  const input = document.createElement('input');
  input.setAttribute('type', 'file');
  input.setAttribute('accept', 'video/*');
  input.click();

  input.onchange = () => {
    const file = input.files[0];
    const reader = new FileReader();

    reader.onload = (e) => {
      const range = this.quill.getSelection();
      this.quill.insertEmbed(range.index, 'video', e.target.result);
    };

    reader.readAsDataURL(file);
  };
};

export const FormEditReseller = ({ reseller }) => {
  const navigate = useNavigate();
  const [panels, setPanels] = useState([]);
  const [allPanels, setAllPanels] = useState([]);
  const [panelsLoading, setPanelsLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const dispatch = useDispatch();
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [autoCompleteOpen, setAutoCompleteOpen] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);// All panels ever loaded
  const [selectedPanels, setSelectedPanels] = useState([]); // Currently selected panels
  const [searchResults, setSearchResults] = useState([]); // Current search results




  // Quill modules configuration with custom image/video handlers
  const quillModules = {
    toolbar: {
      container: [
        [{ header: [1, 2, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'image', 'video'],
        ['clean']
      ],
      handlers: {
        image: ImageHandler,
        video: VideoHandler
      }
    }
  };

  // Quill formats
  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'link',
    'image',
    'video'
  ];

  const [data, setData] = useState({
    name: reseller?.name || "",
    phone: reseller?.phone || "",
    email: reseller?.email || "",
    telegram: reseller?.telegram || "",
    whatsapp: reseller?.whatsapp || "",
    description: reseller?.description || "",
    photo: reseller?.photo || null,
    ads: reseller?.ads || [],
  });

  const [errors, setErrors] = useState({
    name: "",
    contact: "",
  });

  const getSelectedPanels = useCallback(() => {
    // Combine all panels we know about (from allPanels and current panels)
    const allKnownPanels = [...allPanels, ...panels];
    const uniquePanels = allKnownPanels.filter((panel, index, self) =>
        index === self.findIndex(p => p.smmpanel_id === panel.smmpanel_id)
    );

    return uniquePanels.filter(panel =>
        data.selectedPanelIds.includes(panel.smmpanel_id)
    );
  }, [allPanels, panels, data.selectedPanelIds]);

  useEffect(() => {
    if (reseller && reseller.panels) {
      const initialSelectedPanels = reseller.panels.map(p => ({
        smmpanel_id: p.smmpanel_id,
        smmpanel_url: p.smmpanel_url,
        user_id: p.user_id,
        user: p.user
      }));
      setSelectedPanels(initialSelectedPanels);

      // Also add these to allPanels
      setAllPanels(prev => {
        const newPanels = initialSelectedPanels.filter(
            panel => !prev.some(p => p.smmpanel_id === panel.smmpanel_id)
        );
        return [...prev, ...newPanels];
      });
    }
  }, [reseller]);

  const debouncedSearch = useCallback(
      debounce((searchValue) => {
        if (searchValue && searchValue.length > 2) {
          searchPanels(searchValue);
        } else if (!searchValue) {
          // If search is cleared, load first page of all panels
          getAvailablePanels(1, true);
        }
      }, 500),
      [reseller]
  );

  const handleSearchChange = (event, value) => {
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const addToAllPanels = (newPanels) => {
    setAllPanels(prev => {
      const updatedPanels = [...prev];
      newPanels.forEach(newPanel => {
        if (!updatedPanels.some(p => p.smmpanel_id === newPanel.smmpanel_id)) {
          updatedPanels.push(newPanel);
        }
      });
      return updatedPanels;
    });
  };


  // Photo handling
  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setData((prev) => ({ ...prev, photo: file }));
    }
  };

  // Ads management
  const addAd = () => {
    setData((prev) => ({
      ...prev,
      ads: [...prev.ads, { name: "", details: "", photo: null }],
    }));
  };

  const removeAd = (index) => {
    setData((prev) => ({
      ...prev,
      ads: prev.ads.filter((_, i) => i !== index),
    }));
  };

  const handleAdChange = (index, field, value) => {
    setData((prev) => ({
      ...prev,
      ads: prev.ads.map((ad, i) =>
          i === index ? { ...ad, [field]: value } : ad
      ),
    }));
  };

  const handleAdPhotoUpload = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      handleAdChange(index, "photo", file);
    }
  };

  // Input handling
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));

    if (name === "name" && value.trim() !== "") {
      setErrors((prev) => ({ ...prev, name: "" }));
    }
    if (["phone", "email", "telegram", "whatsapp"].includes(name)) {
      const anyFilled = ["phone", "email", "telegram", "whatsapp"].some(
          (k) => data[k].trim() !== ""
      );
      setErrors((prev) => ({
        ...prev,
        contact: anyFilled ? "" : prev.contact,
      }));
    }
  };

  // Editor handlers
  const handleDescriptionChange = (value) => {
    setData((prev) => ({ ...prev, description: value }));
  };

  const handleAdEditorChange = (index, value) => {
    setData((prev) => ({
      ...prev,
      ads: prev.ads.map((ad, i) =>
          i === index ? { ...ad, details: value } : ad
      ),
    }));
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    let hasError = false;
    const newErrors = { name: "", contact: "" };

    if (data.name.trim() === "") {
      newErrors.name = "Name is required";
      hasError = true;
    }

    const contactFilled = ["phone", "email", "telegram", "whatsapp"].some(
        (k) => data[k].trim() !== ""
    );
    if (!contactFilled) {
      newErrors.contact = "At least one contact is required";
      hasError = true;
    }

    setErrors(newErrors);
    if (hasError) return;

    // Prepare form data
    setIsLoadingSubmit(true);
    const formData = new FormData();

    // Main fields
    Object.entries(data).forEach(([key, value]) => {
      if (key === "photo" && value) {
        if (value instanceof File) {
          formData.append(key, value);
        } else if (typeof value === "string") {
          formData.append(key, value);
        }
      }
      if (!["ads", "photo", "id"].includes(key)) {
        formData.append(key, value);
      }
    });

    // Add panels
    selectedPanels.forEach((panel) => {
      formData.append("panel_ids[]", panel.smmpanel_id);
    });

    // Add ads
    data.ads.forEach((ad, index) => {
      formData.append(`ads[${index}][name]`, ad.name);
      formData.append(`ads[${index}][details]`, ad.details);
      if (ad.photo) {
        if (ad.photo instanceof File) {
          formData.append(`ads[${index}][photo]`, ad.photo);
        } else {
          formData.append(`ads[${index}][photo]`, ad.photo);
        }
      }
      if (ad.id) {
        formData.append(`ads[${index}][id]`, ad.id);
      }
    });

    // Submit
    try {
      const url = `${api_Routes.Resellers.Edit}/${reseller.id}`;

      const { response, message } = await Helper.Post({
        url,
        data: formData,
        hasToken: true,
      });

      if (response) {
        dispatch(setSuccess(true));
        navigate("/Resellers");
      }
    } catch (error) {
      console.error("Submission error:", error);
    } finally {
      setIsLoadingSubmit(false);
    }
  };



  const searchPanels = async (searchValue) => {
    setSearchLoading(true);
    try {
      const { response } = await Helper.Get({
        url: `${api_Routes.Resellers.SearchAvailable}/${reseller.id}?search=${encodeURIComponent(searchValue)}`,
        hasToken: true,
      });

      if (response) {
        const newPanels = response?.data || [];

        // Add search results to allPanels
        addToAllPanels(newPanels);

        setSearchResults(newPanels);
        setHasMorePages(false); // Disable load more during search
      }
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setSearchLoading(false);
    }
  };

  const getAvailablePanels = async (page = 1, reset = false) => {
    if (!reseller || !reseller.id) return;

    if (reset) {
      setSearchResults([]);
      setCurrentPage(1);
      setSearchTerm('');
    }

    setPanelsLoading(true);
    try {
      const { response } = await Helper.Get({
        url: `${api_Routes.Resellers.AvailablePanels}/${reseller.id}?page=${page}`,
        hasToken: true,
      });

      if (response) {
        const newPanels = response?.data?.data || [];

        // Add new panels to allPanels
        addToAllPanels(newPanels);

        if (reset) {
          setSearchResults(newPanels);
        } else {
          setSearchResults(prev => [...prev, ...newPanels]);
        }

        setHasMorePages(response?.data?.next_page_url !== null);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setPanelsLoading(false);
    }
  };


  const loadMorePanels = () => {
    getAvailablePanels(currentPage + 1);
  };

  useEffect(() => {
    if (!reseller || !reseller.id) return;
    getAvailablePanels(1, true);
  }, [reseller]);

  const handlePanelSelect = (_, newValue) => {
    setSelectedPanels(newValue);
  };

  // Handle panel removal via chip delete
  const handlePanelRemove = (panelId) => {
    setSelectedPanels(prev => prev.filter(panel => panel.smmpanel_id !== panelId));
  };

  useEffect(() => {
    if (!reseller || !reseller.id) return;
    getAvailablePanels(1, true);
  }, [reseller]);

  return (
      <form onSubmit={handleSubmit} noValidate>
        <Box display="flex" flexDirection="column" gap={2}>
          {/* Name Field */}
          <TextField
              fullWidth
              label="Name"
              name="name"
              value={data.name}
              onChange={handleInputChange}
              required
              error={Boolean(errors.name)}
              helperText={errors.name}
          />

          {/* Reseller Photo */}
          <Box>
            <input
                accept="image/*"
                hidden
                id="reseller-photo"
                type="file"
                onChange={handlePhotoUpload}
            />
            <label htmlFor="reseller-photo">
              <Button variant="outlined" component="span">
                Upload Photo
              </Button>
            </label>
            {data.photo && (
                <Typography variant="caption" ml={2}>
                  {typeof data.photo === "string" ? data.photo : data.photo.name}
                </Typography>
            )}
          </Box>

          {/* Contact Fields */}
          <TextField
              fullWidth
              label="Phone"
              name="phone"
              value={data.phone}
              onChange={handleInputChange}
              autoComplete="off"
          />
          <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={data.email}
              onChange={handleInputChange}
              autoComplete="off"
          />
          <TextField
              fullWidth
              label="Telegram"
              name="telegram"
              value={data.telegram}
              onChange={handleInputChange}
              autoComplete="off"
          />
          <TextField
              fullWidth
              label="Whatsapp"
              name="whatsapp"
              value={data.whatsapp}
              onChange={handleInputChange}
              autoComplete="off"
          />
          {errors.contact && (
              <FormHelperText error>{errors.contact}</FormHelperText>
          )}

          {/* Panels Multi-select */}
          <Autocomplete
              multiple
              options={searchTerm.length > 2 ? searchResults : allPanels}
              getOptionLabel={(option) =>
                  `${option.smmpanel_url} (User: ${option.user?.name || 'Unknown'})`
              }
              value={selectedPanels}
              open={autoCompleteOpen}
              onOpen={() => {
                setAutoCompleteOpen(true);
              }}
              onClose={() => {
                setAutoCompleteOpen(false);
              }}
              disableCloseOnSelect
              onChange={handlePanelSelect}
              inputValue={searchTerm}
              onInputChange={handleSearchChange}
              isOptionEqualToValue={(option, value) =>
                  option.smmpanel_id === value.smmpanel_id
              }
              filterOptions={(options, state) => options} // We're handling filtering server-side
              renderInput={(params) => (
                  <TextField
                      {...params}
                      label="Select Panels"
                      placeholder="Search by panel URL, username, or email..."
                      helperText="Only panels not assigned to other resellers are shown. Selecting a panel will automatically assign its user to this reseller."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                              {searchLoading || panelsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                        ),
                      }}
                  />
              )}
              loading={panelsLoading || searchLoading}
              renderOption={(props, option) => (
                  <li {...props} key={option.smmpanel_id}>
                    <Box>
                      <Typography variant="body1">
                        {option.smmpanel_url}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        User: {option.user?.name || 'Unknown'} ({option.user?.email || 'No email'})
                      </Typography>
                    </Box>
                  </li>
              )}
              renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                      <Chip
                          label={`${option.smmpanel_url} (${option.user?.name || 'Unknown'})`}
                          {...getTagProps({ index })}
                          key={option.smmpanel_id}
                          onDelete={() => handlePanelRemove(option.smmpanel_id)}
                      />
                  ))
              }
              sx={{ mt: 2 }}
          />

          {/* Load More Button */}
          {hasMorePages && !searchTerm && (
              <Box display="flex" justifyContent="center" mt={1}>
                <Button
                    onClick={loadMorePanels}
                    disabled={panelsLoading}
                    variant="outlined"
                    sx={{ width: '200px' }}
                >
                  {panelsLoading ? <CircularProgress size={24} /> : 'Load More'}
                </Button>
              </Box>
          )}

          {/* Description Editor with base64 image/video support */}
          <Box>
            <Typography mb={1}>Description</Typography>
            <ReactQuill
                theme="snow"
                value={data.description}
                onChange={handleDescriptionChange}
                modules={quillModules}
                formats={quillFormats}
                style={{ height: '200px', marginBottom: '40px' }}
            />
          </Box>

          {/* Ads Section */}
          <Typography variant="h6">Ads</Typography>
          {data.ads.map((ad, index) => (
              <Box
                  key={ad.id || index}
                  sx={{ border: 1, p: 2, borderRadius: 2, mb: 2 }}
              >
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="subtitle1">Ad #{index + 1}</Typography>
                  <IconButton onClick={() => removeAd(index)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>

                <TextField
                    fullWidth
                    label="Ad Name"
                    value={ad.name}
                    onChange={(e) => handleAdChange(index, "name", e.target.value)}
                    sx={{ mb: 2 }}
                />

                {/* Ad Details Editor with base64 image/video support */}
                <Box mb={2}>
                  <Typography mb={1}>Ad Details</Typography>
                  <ReactQuill
                      theme="snow"
                      value={ad.details}
                      onChange={(value) => handleAdEditorChange(index, value)}
                      modules={quillModules}
                      formats={quillFormats}
                      style={{ height: '200px', marginBottom: '40px' }}
                  />
                </Box>

                <input
                    accept="image/*"
                    hidden
                    id={`ad-photo-${index}`}
                    type="file"
                    onChange={(e) => handleAdPhotoUpload(index, e)}
                />
                <label htmlFor={`ad-photo-${index}`}>
                  <Button variant="outlined" component="span">
                    Upload Ad Image
                  </Button>
                </label>
                {ad.photo && (
                    <Typography variant="caption" ml={2}>
                      {typeof ad.photo === "string" ? ad.photo : ad.photo.name}
                    </Typography>
                )}
              </Box>
          ))}

          <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addAd}
              sx={{ width: 200 }}
          >
            Add Ad
          </Button>

          {/* Submit Button */}
          <Button
              type="submit"
              variant="contained"
              disabled={isLoadingSubmit}
              sx={{
                mt: 2,
                width: "20%",
                mx: "auto",
                fontWeight: "bold",
              }}
          >
            {isLoadingSubmit ? <CircularProgress size={24} /> : "Submit"}
          </Button>
        </Box>
      </form>
  );
};